<template>
  <div class="MemberProfileFlow">
    <!-- Reminder on the threads page -->
    <div v-if="reminderVisible" class="MemberProfileFlow__reminder" data-e2e="memberProfileReminder">
      <div class="MemberProfileFlow__reminder-text">
        {{ $t('forum.member_profile.reminder') }}
      </div>
      <LLButton class="MemberProfileFlow__reminder-action" primary-transparent small @click="resume">
        {{ $t('forum.member_profile.update_profile') }}
      </LLButton>
      <LLButton
        class="MemberProfileFlow__reminder-dismiss"
        primary-transparent
        tiny
        no-x-padding
        :aria-label="$t('button_close')"
        @click="dismissReminder"
      >
        <CloseRegularIcon />
      </LLButton>
    </div>

    <!-- Flow overlay -->
    <LLConfirmModalTransition v-if="view">
      <LLConfirmModal
        data-e2e="memberProfileFlow"
        :max-width="420"
        :opened="true"
        fixed-width
        @update:opened="close"
      >
        <!-- Step indicator sits above the header (and above the eyebrow). -->
        <template #top>
          <div v-if="stepsVisible" class="MemberProfileFlow__steps">
            <LLLineProgress :progress-list="stepsProgress" />
          </div>
        </template>

        <!-- The eyebrow lives in the modal header. There is no close button: the flow is
             dismissed only by its own actions, and backdrop tap-to-close stays off. -->
        <template #title>
          <span v-if="eyebrowText" class="MemberProfileFlow__eyebrow">{{ eyebrowText }}</span>
        </template>

        <!-- Invitation -->
        <template v-if="view === 'invitation'">
          <h3 class="MemberProfileFlow__title">{{ $t('forum.member_profile.invitation_title') }}</h3>
          <p class="MemberProfileFlow__lead">{{ $t('forum.member_profile.invitation_lead') }}</p>
          <div class="MemberProfileFlow__actions">
            <LLButton class="flex-1" primary @click="goStep1">
              {{ $t('forum.member_profile.set_up') }}
            </LLButton>
            <LLButton class="MemberProfileFlow__action-secondary" tertiary @click="decline">
              {{ $t('forum.member_profile.not_now') }}
            </LLButton>
          </div>
        </template>

        <!-- Check-in -->
        <template v-else-if="view === 'checkin'">
          <h3 class="MemberProfileFlow__title">{{ $t('forum.member_profile.checkin_title') }}</h3>
          <p class="MemberProfileFlow__lead">{{ $t('forum.member_profile.checkin_lead') }}</p>
          <div class="MemberProfileFlow__preview">
            <div class="MemberProfileFlow__preview-head">
              <ProspectAvatar
                class="MemberProfileFlow__preview-avatar"
                :prospect="user"
                disable-tooltip
                disable-new-sms-indicator
              />
              <div class="MemberProfileFlow__preview-identity">
                <div class="MemberProfileFlow__preview-name">{{ user.fullNameWithShortLastName }}</div>
                <div class="MemberProfileFlow__preview-role">
                  {{ $t('forum.member_profile.member_role') }}
                </div>
              </div>
            </div>
            <div class="MemberProfileFlow__preview-body">
              <div v-if="user.fullLocation" class="MemberProfileFlow__preview-row">
                <span class="MemberProfileFlow__preview-label">{{
                  $t('forum.member_profile.location')
                }}</span>
                <span class="MemberProfileFlow__preview-value">{{ user.fullLocation }}</span>
              </div>
              <div v-if="ageBandLabel" class="MemberProfileFlow__preview-row">
                <span class="MemberProfileFlow__preview-label">{{ $t('forum.member_profile.age') }}</span>
                <span class="MemberProfileFlow__preview-value">{{ ageBandLabel }}</span>
              </div>
              <div v-if="user.occupation" class="MemberProfileFlow__preview-row">
                <span class="MemberProfileFlow__preview-label">{{ $t('forum.member_profile.does') }}</span>
                <span class="MemberProfileFlow__preview-value">{{ user.occupation }}</span>
              </div>
              <div v-if="user.aboutMe" class="MemberProfileFlow__preview-bio">{{ user.aboutMe }}</div>
            </div>
          </div>
          <div class="MemberProfileFlow__actions MemberProfileFlow__actions_stack">
            <LLButton class="w-full" primary @click="looksGood">
              {{ $t('forum.member_profile.looks_good') }}
            </LLButton>
            <LLButton class="w-full mt-2" darkned @click="openEditProfile">
              {{ $t('forum.member_profile.edit_profile') }}
            </LLButton>
          </div>
        </template>

        <!-- Step 1 -->
        <template v-else-if="view === 'step1'">
          <h3 class="MemberProfileFlow__title">{{ $t('forum.member_profile.prefill_title') }}</h3>
          <div class="MemberProfileFlow__preview">
            <div class="MemberProfileFlow__preview-body">
              <div class="MemberProfileFlow__preview-row">
                <span class="MemberProfileFlow__preview-label">{{
                  $t('forum.member_profile.name_shown')
                }}</span>
                <span class="MemberProfileFlow__preview-value">{{ user.fullNameWithShortLastName }}</span>
              </div>
              <div v-if="user.fullLocation" class="MemberProfileFlow__preview-row">
                <span class="MemberProfileFlow__preview-label">{{
                  $t('forum.member_profile.location')
                }}</span>
                <span class="MemberProfileFlow__preview-value">{{ user.fullLocation }}</span>
              </div>
              <div v-if="ageBandLabel" class="MemberProfileFlow__preview-row">
                <span class="MemberProfileFlow__preview-label">{{ $t('forum.member_profile.age') }}</span>
                <span class="MemberProfileFlow__preview-value">{{ ageBandLabel }}</span>
              </div>
            </div>
          </div>
          <div class="MemberProfileFlow__actions">
            <LLButton class="w-full" primary @click="goStep2">
              {{ $t('forum.member_profile.continue') }}
            </LLButton>
          </div>
        </template>

        <!-- Step 2 / edit form -->
        <template v-else-if="view === 'step2'">
          <h3 class="MemberProfileFlow__title">{{ $t('forum.member_profile.about_title') }}</h3>
          <p class="MemberProfileFlow__lead">{{ $t('forum.member_profile.about_lead') }}</p>
          <div class="MemberProfileFlow__field">
            <LLInputText
              v-model="form.occupation"
              :max-length="60"
              :placeholder="$t('forum.member_profile.occupation_placeholder')"
              data-e2e="memberProfileOccupation"
            >
              <template #caption>{{ $t('forum.member_profile.occupation_label') }}</template>
            </LLInputText>
          </div>
          <div class="MemberProfileFlow__field">
            <LLTextarea
              v-model="form.aboutMe"
              size="compact"
              dynamic-size
              plain-text
              hide-menu-bar
              hide-emoji
              :max-height="200"
              :max-length="200"
              :placeholder="$t('forum.member_profile.about_placeholder')"
              data-e2e="memberProfileAboutMe"
            >
              <template #caption>
                <span class="MemberProfileFlow__caption-row">
                  <span>
                    {{ $t('forum.member_profile.about_label') }}
                    <span class="MemberProfileFlow__caption-optional">{{
                      $t('forum.member_profile.about_optional')
                    }}</span>
                  </span>
                  <span class="MemberProfileFlow__counter">{{ (form.aboutMe || '').length }} / 200</span>
                </span>
              </template>
            </LLTextarea>
          </div>
          <div class="MemberProfileFlow__actions">
            <LLButton class="flex-1" primary :disabled="!canContinueStep2 || saving" @click="goPreview">
              {{ $t('forum.member_profile.continue') }}
            </LLButton>
            <LLButton
              v-if="isResume"
              class="MemberProfileFlow__action-secondary"
              tertiary
              @click="finishLater"
            >
              {{ $t('forum.member_profile.finish_later') }}
            </LLButton>
          </div>
        </template>

        <!-- Preview -->
        <template v-else-if="view === 'preview'">
          <h3 class="MemberProfileFlow__title">{{ $t('forum.member_profile.preview_title') }}</h3>
          <div class="MemberProfileFlow__preview">
            <div class="MemberProfileFlow__preview-head">
              <ProspectAvatar
                class="MemberProfileFlow__preview-avatar"
                :prospect="user"
                disable-tooltip
                disable-new-sms-indicator
              />
              <div class="MemberProfileFlow__preview-identity">
                <div class="MemberProfileFlow__preview-name">{{ user.fullNameWithShortLastName }}</div>
                <div class="MemberProfileFlow__preview-role">
                  {{ $t('forum.member_profile.member_role') }}
                </div>
              </div>
            </div>
            <div class="MemberProfileFlow__preview-body">
              <div v-if="user.fullLocation" class="MemberProfileFlow__preview-row">
                <span class="MemberProfileFlow__preview-label">{{
                  $t('forum.member_profile.location')
                }}</span>
                <span class="MemberProfileFlow__preview-value">{{ user.fullLocation }}</span>
              </div>
              <div v-if="ageBandLabel" class="MemberProfileFlow__preview-row">
                <span class="MemberProfileFlow__preview-label">{{ $t('forum.member_profile.age') }}</span>
                <span class="MemberProfileFlow__preview-value">{{ ageBandLabel }}</span>
              </div>
              <div v-if="form.occupation" class="MemberProfileFlow__preview-row">
                <span class="MemberProfileFlow__preview-label">{{ $t('forum.member_profile.does') }}</span>
                <span class="MemberProfileFlow__preview-value">{{ form.occupation }}</span>
              </div>
              <div v-if="form.aboutMe" class="MemberProfileFlow__preview-bio">{{ form.aboutMe }}</div>
            </div>
          </div>
          <div class="MemberProfileFlow__actions">
            <LLButton class="flex-1" primary :loading="saving" @click="saveAndFinish">
              {{ $t('forum.member_profile.save_continue') }}
            </LLButton>
            <LLButton class="MemberProfileFlow__action-secondary" tertiary @click="view = 'step2'">
              {{ $t('forum.member_profile.back') }}
            </LLButton>
          </div>
        </template>

        <!-- Done -->
        <template v-else-if="view === 'done'">
          <SuccessIcon class="MemberProfileFlow__done-check" />
          <h3 class="MemberProfileFlow__title MemberProfileFlow__title_center">
            {{ $t('forum.member_profile.done_title') }}
          </h3>
          <p class="MemberProfileFlow__lead MemberProfileFlow__lead_center">
            {{ $t('forum.member_profile.done_lead') }}
          </p>
          <div class="MemberProfileFlow__actions">
            <LLButton class="w-full" primary @click="close">
              {{ $t('forum.member_profile.go_to_chat') }}
            </LLButton>
          </div>
        </template>
      </LLConfirmModal>
    </LLConfirmModalTransition>
  </div>
</template>

<script>
import LLButton from '@/components/common/ui-components/LLButton.vue'
import LLConfirmModal from '@/components/common/LLConfirmModal.vue'
import LLConfirmModalTransition from '@/components/common/LLConfirmModal/LLConfirmModalTransition.vue'
import LLLineProgress from '@/components/common/ui-components/LLLineProgress.vue'
import LLInputText from '@/components/common/LLInput/LLInputText'
import LLTextarea from '@/components/common/LLTextarea.vue'
import ProspectAvatar from '@/components/modules/prospects/ProspectAvatar.vue'
import CloseRegularIcon from '@/assets/icons/CloseRegularIcon.vue'
import SuccessIcon from '@/assets/icons/SuccessIcon.vue'
import { ProspectModel } from '@/models/prospect'
import { ChatProfileSetupState } from '@/common/enums'
import { splitAgeBandLabel } from '@/utils/ageGroups'

export default {
  name: 'MemberProfileFlow',
  components: {
    LLButton,
    LLConfirmModal,
    LLConfirmModalTransition,
    LLLineProgress,
    LLInputText,
    LLTextarea,
    ProspectAvatar,
    CloseRegularIcon,
    SuccessIcon
  },
  props: {
    chat: { type: Object, required: true },
    // The flow is mounted on every chat page for its modal, but the reminder banner is only
    // rendered where it has a natural place (chat main page, thread page).
    withReminder: { type: Boolean, default: false }
  },
  data() {
    return {
      state: null,
      view: null,
      isResume: false,
      saving: false,
      draftSaving: false,
      form: { occupation: '', aboutMe: '' }
    }
  },
  computed: {
    user() {
      return ProspectModel.parseFromApi({ prospect: this.$store.getters['user/user'] || {} })
    },
    reminderVisible() {
      if (!this.withReminder) return false
      if (!this.state || !this.state.isSetupEnabled) return false
      if (!this.state.promptSeen) return false
      if (this.state.state === ChatProfileSetupState.COMPLETED) return false
      if (this.state.reminderDismissed) return false
      // Stays visible while the flow is open: hiding it on "Update profile" made the page
      // behind the modal jump. It disappears only on COMPLETED (or when dismissed).
      return true
    },
    canContinueStep2() {
      return !!(this.form.occupation || '').trim()
    },
    stepsProgress() {
      return [{ name: 'member-profile', color: 'accent-01', progress: this.view === 'step1' ? 50 : 100 }]
    },
    // The indicator covers the two wizard steps, but not the resume/edit flow (there is no step 1 then).
    stepsVisible() {
      return this.view === 'step1' || (this.view === 'step2' && !this.isResume)
    },
    // Rendered in the modal header (left of the close button) instead of the body.
    eyebrowText() {
      switch (this.view) {
        case 'invitation': {
          const chatName = (this.chat?.name || '').trim()
          return chatName
            ? this.$t('forum.member_profile.invitation_eyebrow_with_chat', { chat: chatName })
            : this.$t('forum.member_profile.invitation_eyebrow')
        }
        case 'checkin':
          return this.$t('forum.member_profile.checkin_eyebrow')
        case 'step1':
          return this.$t('forum.member_profile.step_1_of_2')
        case 'step2':
          return this.$t(
            this.isResume ? 'forum.member_profile.resume_eyebrow' : 'forum.member_profile.step_2_of_2'
          )
        case 'preview':
          return this.$t('forum.member_profile.almost_done')
        default:
          return ''
      }
    },
    ageGroups() {
      return (
        this.chat?.quantAgeSetting ||
        this.$store.state.appData?.quantAgeGroups || {
          '14-26': [14, 26],
          '27-40': [27, 40],
          '41-59': [41, 59],
          '60+': [60]
        }
      )
    },
    ageBandLabel() {
      const age = this.user?.age
      if (age == null) return null
      const entry = Object.entries(this.ageGroups).find(([, range]) => {
        const from = range?.[0]
        const to = range?.[1]
        return (from == null || age >= from) && (to == null || age <= to)
      })
      const band = entry?.[0]
      if (!band) return null
      const range = splitAgeBandLabel(band)
      return range ? this.$t('forum.member_profile.age_range', range) : band
    }
  },
  mounted() {
    this.fetchState()
  },
  beforeDestroy() {
    // Persist partial progress if the member leaves the chat while mid-flow.
    if (this.view === 'step2' || this.view === 'preview') this.persistDraft()
  },
  methods: {
    async fetchState() {
      if (!this.chat?.id) return
      try {
        const { profileState } = await this.$api.forum.chats.getProfileState({ chatId: this.chat.id })
        this.state = profileState
        await this.decideInitialView()
      } catch (e) {
        // Silent: the prompt is non-critical
      }
    },
    async decideInitialView() {
      if (!this.state || !this.state.isSetupEnabled || this.state.promptSeen) return
      this.view = this.state.hasProfile ? 'checkin' : 'invitation'
      // The prompt only ever appears on the first entry to a chat. Stamp it as seen the
      // moment it is displayed, so closing or abandoning it never re-asks on re-entry.
      await this.markPromptSeen()
    },
    async markPromptSeen() {
      if (!this.state || this.state.promptSeen) return
      try {
        const { profileState } = await this.$api.forum.chats.updateProfileState({
          chatId: this.chat.id,
          promptSeen: true
        })
        this.state = profileState
      } catch (e) {
        // ignore
      }
    },
    async setState(state) {
      try {
        const { profileState } = await this.$api.forum.chats.updateProfileState({
          chatId: this.chat.id,
          state
        })
        this.state = profileState
      } catch (e) {
        // ignore
      }
    },
    async goStep1() {
      await this.markPromptSeen()
      this.isResume = false
      this.view = 'step1'
    },
    async goStep2() {
      await this.setState(ChatProfileSetupState.IN_PROGRESS)
      this.form.occupation = this.user.occupation || ''
      this.form.aboutMe = this.user.aboutMe || ''
      this.isResume = false
      this.view = 'step2'
    },
    goPreview() {
      if (!this.canContinueStep2) return
      this.view = 'preview'
    },
    async saveAndFinish() {
      this.saving = true
      try {
        await this.$store.dispatch('user/syncUser', {
          occupation: (this.form.occupation || '').trim(),
          aboutMe: this.form.aboutMe
        })
        await this.setState(ChatProfileSetupState.COMPLETED)
        this.view = 'done'
      } catch (e) {
        this.$errorAlert(e)
      }
      this.saving = false
    },
    // Persist the in-progress form so an abandoned flow resumes with the member's input
    // instead of starting over. Best effort: a failed save must not block closing.
    async persistDraft() {
      if (this.draftSaving) return
      if (!this.form.occupation && !this.form.aboutMe) return
      this.draftSaving = true
      try {
        await this.$store.dispatch('user/syncUser', {
          occupation: (this.form.occupation || '').trim() || null,
          aboutMe: this.form.aboutMe || null
        })
      } catch (e) {
        // best effort
      } finally {
        this.draftSaving = false
      }
    },
    finishLater() {
      this.close()
    },
    async decline() {
      await this.markPromptSeen()
      await this.setState(ChatProfileSetupState.DECLINED)
      this.view = null
    },
    async looksGood() {
      await this.markPromptSeen()
      await this.setState(ChatProfileSetupState.COMPLETED)
      this.view = null
    },
    async openEditProfile() {
      await this.markPromptSeen()
      await this.setState(ChatProfileSetupState.COMPLETED)
      this.view = null
      this.$root.$emit('open-user-edit-modal')
    },
    async resume() {
      await this.markPromptSeen()
      this.form.occupation = this.user.occupation || ''
      this.form.aboutMe = this.user.aboutMe || ''
      this.isResume = true
      // Resume at the unfinished step: reaching step 2 flips the state to IN_PROGRESS,
      // so any other state means the member stopped on (or before) the confirmation step.
      this.view = this.state?.state === ChatProfileSetupState.IN_PROGRESS ? 'step2' : 'step1'
    },
    async dismissReminder() {
      if (!this.state) return
      try {
        const { profileState } = await this.$api.forum.chats.updateProfileState({
          chatId: this.chat.id,
          reminderDismissed: true
        })
        this.state = profileState
      } catch (e) {
        // ignore
      }
    },
    close() {
      const shouldPersist = this.view === 'step2' || this.view === 'preview'
      this.view = null
      // Fire-and-forget: the overlay must dismiss immediately, the save is best effort.
      if (shouldPersist) this.persistDraft()
    }
  }
}
</script>

<style lang="scss" scoped>
.MemberProfileFlow {
  &__reminder {
    @apply flex items-center rounded border border-status-02-200 bg-status-02-25 px-3 py-2 mt-4;
  }
  &__reminder-text {
    @apply text-caption-01 text-neutral-01-600 flex-1;
  }
  &__reminder-action {
    @apply ml-2 flex-shrink-0;
  }
  &__reminder-dismiss {
    @apply ml-3 flex-shrink-0;
  }
  &__steps {
    @apply px-6 pt-6;
  }
  &__eyebrow {
    @apply text-caption-01 uppercase tracking-wider text-neutral-01-400 mb-1;
  }
  &__title {
    @apply text-primary-01 mb-3;
    &_center {
      @apply text-center;
    }
  }
  &__lead {
    @apply text-body-03 text-neutral-01-400 mb-4;
    &_center {
      @apply text-center;
    }
  }
  &__preview {
    @apply rounded border border-neutral-01-50 bg-white mb-4 overflow-hidden;
  }
  &__preview-head {
    @apply flex items-center px-4 py-3 bg-neutral-01-25;
  }
  &__preview-avatar {
    @apply w-12 h-12 rounded-full mr-4 flex-shrink-0;
  }
  &__preview-identity {
    @apply min-w-0;
  }
  &__preview-name {
    @apply text-body-02 text-primary-01;
  }
  &__preview-role {
    @apply text-caption-01 text-neutral-01-400;
  }
  &__preview-body {
    @apply px-4 pt-4 pb-5;
  }
  &__preview-row {
    @apply flex items-baseline;
    & + & {
      @apply mt-4;
    }
  }
  &__preview-label {
    @apply text-caption-01 uppercase tracking-wider text-neutral-01-400 mr-3 flex-shrink-0 w-24;
  }
  &__preview-value {
    @apply text-body-03 text-primary-01 break-words;
  }
  &__preview-row + &__preview-bio {
    @apply mt-4;
  }
  &__preview-bio {
    @apply text-body-03 text-primary-01 break-words;
  }
  &__field {
    @apply mt-4;
  }
  &__caption-row {
    @apply flex items-baseline justify-between;
  }
  &__caption-optional {
    @apply text-neutral-01-400 font-normal;
  }
  &__counter {
    @apply text-caption-02 text-neutral-01-400;
  }
  &__actions {
    @apply flex items-center mt-5;
    &_stack {
      @apply flex-col items-stretch;
    }
  }
  &__action-secondary {
    @apply ml-2;
  }
  &__done-check {
    @apply w-12 h-12 block mx-auto mb-3 text-success-01;
  }
}
</style>
